/**
 * Презентационные хелперы раздела «Клиенты» — чистые функции, без React.
 */

import dayjs from 'dayjs';

import type {ClientListItem, GetClientsRequest, SubscriptionStatus} from '@services/clients';

/** Состояние фильтров списка (единое для десктопа и мобилки). */
export type ClientsFilters = {
  search: string;
  status: SubscriptionStatus | null;
  type: 'solo' | 'clinic' | null;
  cities: string[];
  tariffs: string[];
  page: number;
};

export const EMPTY_FILTERS: ClientsFilters = {
  search: '',
  status: null,
  type: null,
  cities: [],
  tariffs: [],
  page: 1,
};

/** Фильтры → запрос сервиса. */
export const toRequest = (f: ClientsFilters): GetClientsRequest => ({
  status: f.status ?? undefined,
  type: f.type ?? undefined,
  query: f.search.trim() || undefined,
  cities: f.cities.length ? f.cities : undefined,
  tariffs: f.tariffs.length ? f.tariffs : undefined,
  page: f.page,
});

/** Сколько групп фильтров активно (без поиска) — для бейджа на кнопке. */
export const activeFilterCount = (f: ClientsFilters): number =>
  (f.status ? 1 : 0) + (f.type ? 1 : 0) + (f.cities.length ? 1 : 0) + (f.tariffs.length ? 1 : 0);

/** Число выбранных значений (каждый город/тариф считается отдельно). */
export const selectedFilterCount = (f: ClientsFilters): number =>
  (f.status ? 1 : 0) + (f.type ? 1 : 0) + f.cities.length + f.tariffs.length;

/** Опции фильтра «Тип» — общие для десктопа и мобилки. */
export const TYPE_OPTIONS: {value: 'clinic' | 'solo'; label: string}[] = [
  {value: 'clinic', label: 'Клиника'},
  {value: 'solo', label: 'Соло'},
];

export type RenewalTone = 'default' | 'soon' | 'past' | 'none';

/** Дата продления: текст «24 июл» + тон (скоро/просрочено/нет). */
export const renewalMeta = (
  iso: string | null,
  now: Date = new Date(),
): {text: string; tone: RenewalTone} => {
  if (!iso) return {text: '—', tone: 'none'};
  const d = dayjs(iso);
  const text = d.format('D MMM').replace('.', '');
  const days = d.diff(dayjs(now), 'day');
  const tone: RenewalTone = days < 0 ? 'past' : days <= 14 ? 'soon' : 'default';
  return {text, tone};
};

/** Класс цвета текста даты продления по тону. */
const RENEWAL_TONE_CLASS: Record<RenewalTone, string> = {
  default: 'text-skr-ink2',
  soon: 'text-skr-warn',
  past: 'text-skr-danger',
  none: 'text-skr-faint',
};
export const renewalToneClass = (tone: RenewalTone): string => RENEWAL_TONE_CLASS[tone];

/** Подстрока тарифа: «места 7/10» для клиники, «фикс.» для соло. */
export const tariffSub = (c: ClientListItem): string =>
  c.type === 'clinic' ? `места ${c.seatsUsed}/${c.seatsLimit}` : 'фикс.';

export const typeLabel = (type: 'solo' | 'clinic'): string =>
  type === 'solo' ? 'Соло' : 'Клиника';
